/**
 * Binds the OneView session to the current Microsoft (Entra ID) identity.
 *
 * Microsoft is the single source of truth for who the user is. The persisted Redux
 * session is only trusted when this tab's MSAL account is the same identity, and it
 * is rebuilt (with user-scoped state wiped) whenever the Microsoft identity changes.
 *
 * Note: this is client-side session hygiene. The backend must still validate every
 * access token (signature, issuer, audience, expiry, tenant) and derive the user from
 * its claims; nothing here is a security boundary on its own.
 */

import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import type { Dispatch } from "@reduxjs/toolkit";
import { loginSuccess, logout, User } from "@/store/slices/authSlice";
import { clearDashboardState, setHasPreferredLayout } from "@/store/slices/dashboardSlice";
import { clearSearch, clearSelectedPatient } from "@/store/slices/searchSlice";
import { clearUserStatus } from "@/store/slices/userStatusSlice";

const CONFIGURED_TENANT_ID = import.meta.env.VITE_AZURE_TENANT_ID || "";
const MULTI_TENANT_AUTHORITIES = ["", "common", "organizations", "consumers"];

/**
 * The MSAL account this tab is signed in with
 */
export const getCurrentMsalAccount = (
  instance: Pick<IPublicClientApplication, "getActiveAccount" | "getAllAccounts">
): AccountInfo | null => instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;

/**
 * Only accept accounts from the tenant OneView is configured for
 */
export const isAllowedTenant = (account: AccountInfo) =>
  MULTI_TENANT_AUTHORITIES.includes(CONFIGURED_TENANT_ID) ||
  account.tenantId === CONFIGURED_TENANT_ID;

/**
 * Whether the OneView user belongs to this Microsoft account
 */
export const isAccountForUser = (account: AccountInfo | null, user: User | null) =>
  Boolean(
    account &&
      user?.azureAccountId &&
      account.localAccountId === user.azureAccountId &&
      account.tenantId === user.tenantId
  );

/**
 * Map an MSAL account to the Redux user shape
 */
export const mapAccountToUser = (account: AccountInfo): User => ({
  id: 0, // You might want to get this from your backend
  first_name: account.name?.split(" ")[0] || "",
  last_name: account.name?.split(" ").slice(1).join(" ") || "",
  email: account.username || "",
  role: "user", // You might want to get this from token claims or your backend
  azureAccountId: account.localAccountId,
  tenantId: account.tenantId,
});

/**
 * Drop the OneView session and every piece of user-scoped state
 */
export const resetUserScopedState = (dispatch: Dispatch) => {
  dispatch(logout());
  dispatch(clearDashboardState());
  // A different user must not inherit the previous user's layout flag
  dispatch(setHasPreferredLayout(false));
  dispatch(clearSearch());
  dispatch(clearSelectedPatient());
  dispatch(clearUserStatus());
};

/**
 * Create or refresh the OneView session from the current Microsoft account.
 *
 * - Rejects accounts from other tenants
 * - Wipes user-scoped state when the identity differs from the persisted user
 * - Removes other accounts from this tab's MSAL cache, so API tokens can only
 *   ever be issued for the current identity
 *
 * @returns false when the account is not allowed
 */
export const establishSession = (
  instance: IPublicClientApplication,
  dispatch: Dispatch,
  account: AccountInfo,
  previousUser: User | null
): boolean => {
  if (!isAllowedTenant(account)) {
    console.warn("Rejected Microsoft account from an unexpected tenant");
    return false;
  }

  instance.setActiveAccount(account);

  instance
    .getAllAccounts()
    .filter((cached) => cached.homeAccountId !== account.homeAccountId)
    .forEach((other) => {
      instance.clearCache({ account: other }).catch((error) => {
        console.warn("Failed to clear previous account from MSAL cache:", error);
      });
    });

  if (previousUser && !isAccountForUser(account, previousUser)) {
    resetUserScopedState(dispatch);
  }

  dispatch(loginSuccess(mapAccountToUser(account)));
  return true;
};
